import { ofType } from 'redux-observable';
import {
   ignoreElements,
   tap,
   filter,
   mapTo,
   map,
   delay,
   takeUntil,
   take,
   switchMap,
   distinctUntilKeyChanged,
   mergeMap,
   groupBy,
   debounceTime,
   distinctUntilChanged,
   pairwise,
} from 'rxjs/operators';
import { of, merge, fromEvent } from 'rxjs';

import {
   TOGGLE_REFERENCE_PLAYING,
   PLAY_NOTES,
   playNotes,
   pianoKeyDown,
   PIANO_KEY_DOWN,
   pianoKeyUp,
   PIANO_KEY_UP,
   toggleNoteSelected,
   DOCUMENT_KEYDOWN,
   DOCUMENT_KEYUP,
   TOGGLE_SELECTED_PLAYING,
   STOP_PLAYING,
   stopPlaying,
   PIANO_TOUCH_START,
} from './actions';
import { audioContext } from '../../webAudio/audioContext';
import { keyMappings } from './constants';
import { loadSoundKitEpic } from '../../shared/epics';
import { createSynthNode } from '../../webAudio/createSynthNode';
import { leftClickActionFilter } from '../../util/leftClickActionFilter';
import { combineInterfaceEpics } from '../combineInterfaceEpics';
import { keyboardCompressor } from './keyboardCompressor';
import {
   CLEAR_ACTIVE_INTERFACE,
   SET_ACTIVE_INTERFACE,
} from '../../app/actions';
import { SET_STAGE } from '../../exercises/actions';

const noteSet = new Set();

if (process.env.NODE_ENV === 'development') {
   window.noteSet = noteSet;
}

const stopNotes = () => noteSet.forEach((node) => node.stop());

const playReferenceEpic = (action$, state$) =>
   action$.pipe(
      ofType(TOGGLE_REFERENCE_PLAYING),
      filter(() => state$.value.activeInterface.referencePlaying),
      map(() =>
         playNotes(
            Object.keys(state$.value.activeInterface.referenceNotes).map(
               Number
            ),
            state$.value.activeInterface.referenceInstrument
         )
      )
   );

const stopReferenceEpic = (action$, state$) =>
   action$.pipe(
      ofType(TOGGLE_REFERENCE_PLAYING),
      filter(() => !state$.value.activeInterface.referencePlaying),
      tap(stopNotes),
      ignoreElements()
   );

const playSelectedEpic = (action$, state$) =>
   action$.pipe(
      ofType(TOGGLE_SELECTED_PLAYING),
      filter(() => state$.value.activeInterface.selectedPlaying),
      map(() =>
         playNotes(
            Object.entries(state$.value.activeInterface.selectedNotes)
               .filter(([, isSelected]) => isSelected)
               .map(([note]) => Number(note)),
            state$.value.activeInterface.userInstrument
         )
      )
   );

const stopSelectedEpic = (action$, state$) =>
   action$.pipe(
      ofType(TOGGLE_SELECTED_PLAYING),
      filter(() => !state$.value.activeInterface.selectedPlaying),
      tap(stopNotes),
      ignoreElements()
   );

const playNotesEpic = (action$, state$) =>
   action$.pipe(
      ofType(PLAY_NOTES),
      tap((action) => {
         stopNotes();
         action.notes.forEach((note) => {
            const sound =
               state$.value.activeInterface.soundKit?.sounds?.[
                  action.instrumentName
               ];

            // don't break epics if playing before soundkit loads
            if (!sound) {
               return;
            }

            const synthNode = createSynthNode(sound, note);

            // maybe a memory leak?... replace with a master volume for keyboard?... maybe just disconect on synth end
            const gainNode = audioContext.createGain();
            gainNode.connect(keyboardCompressor);
            gainNode.gain.setValueAtTime(0.5, audioContext.currentTime);
            //

            synthNode.connect(gainNode);
            synthNode.start();
            noteSet.add(synthNode);
            synthNode.onended = () => noteSet.delete(synthNode);
         });
      }),
      ignoreElements()
   );

const touchMoves = fromEvent(document, 'pointermove');
const touchEnd = fromEvent(document, 'pointerup');
const touchCancel = fromEvent(document, 'pointercancel');
const touchEndEvents = merge(touchEnd, touchCancel).pipe(
   filter((e) => leftClickActionFilter({ mouseButton: e.button }))
);

const pianoTouchEventsEpic = (action$) =>
   action$.pipe(
      ofType(PIANO_TOUCH_START),
      filter(leftClickActionFilter),
      switchMap((action) =>
         merge(
            of(null),
            of(action),
            touchMoves.pipe(
               map((e) => {
                  if (e == null) {
                     return null;
                  }

                  return {
                     x: e.clientX,
                     y: e.clientY,
                  };
               }),
               takeUntil(touchEndEvents.pipe(take(1)))
            )
         )
      ),
      map((e) => {
         if (e == null) {
            return null;
         }
         const note = document.elementFromPoint(e.x, e.y)?.dataset.note;

         if (note == null) {
            return null;
         }

         return Number(note);
      }),
      distinctUntilChanged(),
      pairwise(),
      switchMap(([previousNote, currentNote]) =>
         merge(
            of(previousNote != null ? pianoKeyUp(previousNote) : null),
            of(currentNote != null ? pianoKeyDown(currentNote) : null),
            touchEndEvents.pipe(
               take(1),
               mapTo(currentNote != null ? pianoKeyUp(currentNote) : null)
            )
         )
      ),
      filter((action) => action != null)
   );

const pianoKeyboardEventsEpic = (action$) =>
   action$.pipe(
      ofType(DOCUMENT_KEYDOWN, DOCUMENT_KEYUP),
      groupBy((action) => action.code),
      mergeMap((group$) => group$.pipe(distinctUntilKeyChanged('type'))),
      filter(
         (action) =>
            action.type === DOCUMENT_KEYDOWN && keyMappings[action.code] != null
      ),
      mergeMap((action) =>
         merge(
            of(pianoKeyDown(keyMappings[action.code])),
            action$.pipe(
               ofType(DOCUMENT_KEYUP),
               filter(({ code }) => code === action.code),
               mapTo(pianoKeyUp(keyMappings[action.code])),
               take(1)
            )
         )
      )
   );

const pianoKeyEpic = (action$, state$) =>
   action$.pipe(
      ofType(PIANO_KEY_DOWN, PIANO_KEY_UP),
      groupBy((action) => action.note),
      mergeMap((group$) => group$.pipe(distinctUntilKeyChanged('type'))),
      ofType(PIANO_KEY_DOWN),
      mergeMap((action) =>
         merge(
            of(
               playNotes(
                  [action.note],
                  state$.value.activeInterface.userInstrument
               )
            ),
            of({}).pipe(
               filter(() => !state$.value.activeInterface.compareAnswer),
               delay(500),
               map(() => toggleNoteSelected(action.note)),
               takeUntil(
                  action$.pipe(
                     ofType(PIANO_KEY_UP),
                     filter(({ note }) => note === action.note)
                  )
               )
            )
         )
      )
   );

const stopAllEpic = (action$) =>
   action$.pipe(
      ofType(CLEAR_ACTIVE_INTERFACE, SET_ACTIVE_INTERFACE, STOP_PLAYING),
      filter((action) => !action.allowSustain),
      tap(stopNotes),
      ignoreElements()
   );

const stopPlayingEpic = (action$) =>
   merge(
      action$.pipe(ofType(SET_STAGE)),
      action$.pipe(
         ofType(
            TOGGLE_REFERENCE_PLAYING,
            TOGGLE_SELECTED_PLAYING,
            SET_STAGE,
            PIANO_KEY_DOWN
         ),
         debounceTime(8000),
         ofType(TOGGLE_REFERENCE_PLAYING, TOGGLE_SELECTED_PLAYING)
      )
   ).pipe(mapTo(stopPlaying()));

export const keyboardEpics = combineInterfaceEpics(
   playReferenceEpic,
   stopReferenceEpic,
   playSelectedEpic,
   stopSelectedEpic,
   playNotesEpic,
   pianoKeyEpic,
   pianoKeyboardEventsEpic,
   loadSoundKitEpic,
   stopAllEpic,
   stopPlayingEpic,
   pianoTouchEventsEpic
);
